import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge/Badge';
import {
  HISTORY_CHANGE_TONE_STYLES,
  historyChangeTone,
  type HistoryChangeTone,
} from './historyChangeTone';
import {
  formatSessionCreatedLabel,
  formatSessionDeletedLabel,
  formatSessionRestoredLabel,
} from './formatSessionLifecycleLabel';
import type { HistoryDisplayModel } from './types';

function formatAttributeLabel(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return value;
  return trimmed[0]!.toUpperCase() + trimmed.slice(1);
}

function ChangeArrow() {
  return (
    <i
      className="fa-solid fa-arrow-right-long project-change-history-popover__diff-arrow"
      aria-hidden
    />
  );
}

function ToneBadge({ children, tone }: { children: string; tone: HistoryChangeTone }) {
  const style = HISTORY_CHANGE_TONE_STYLES[tone];
  return (
    <Badge variant="custom" bg={style.bg} color={style.color}>
      {children}
    </Badge>
  );
}

function DiffValue({
  children,
  kind,
  tone,
}: {
  children: string;
  kind: 'before' | 'after';
  tone?: HistoryChangeTone;
}) {
  if (kind === 'after' && tone) {
    return (
      <span className="project-change-history-popover__diff-value">
        <ToneBadge tone={tone}>{children}</ToneBadge>
      </span>
    );
  }

  return (
    <span className="project-change-history-popover__diff-value">
      <Badge
        variant="custom"
        bg="color-mix(in srgb, var(--text-muted) 14%, transparent)"
        color="var(--text-muted)"
      >
        {children}
      </Badge>
    </span>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <span className="project-change-history-popover__field-label">
      <Badge
        variant="custom"
        bg="color-mix(in srgb, var(--accent) 12%, transparent)"
        color="var(--accent-hover)"
      >
        {children}
      </Badge>
    </span>
  );
}

function ChangeRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="project-change-history-popover__changes-row">
      <div className="project-change-history-popover__changes-row-label">
        <FieldLabel>{label}</FieldLabel>
      </div>
      <div className="project-change-history-popover__changes-row-diff">{children}</div>
    </div>
  );
}

function DiffLine({ children }: { children: ReactNode }) {
  return <span className="project-change-history-popover__diff-line">{children}</span>;
}

function RelationEntryDiff({
  entry,
}: {
  entry: Extract<HistoryDisplayModel, { type: 'relation_change' }>['entries'][number];
}) {
  if (entry.kind === 'attribute_diff') {
    const tone = historyChangeTone(entry.before, entry.after);
    const attributeLabel = formatAttributeLabel(entry.attribute);
    return (
      <DiffLine>
        <FieldLabel>{entry.entityName}</FieldLabel>
        <DiffValue kind="before">{`${attributeLabel}: ${entry.before}`}</DiffValue>
        <ChangeArrow />
        <DiffValue kind="after" tone={tone}>{`${attributeLabel}: ${entry.after}`}</DiffValue>
      </DiffLine>
    );
  }
  if (entry.kind === 'list_before_after') {
    const tone = historyChangeTone(entry.before, entry.after);
    return (
      <DiffLine>
        <DiffValue kind="before">{entry.before}</DiffValue>
        <ChangeArrow />
        <DiffValue kind="after" tone={tone}>
          {entry.after}
        </DiffValue>
      </DiffLine>
    );
  }
  return (
    <DiffLine>
      <span className="project-change-history-popover__change-line_muted">{entry.message}</span>
    </DiffLine>
  );
}

export function HistoryChangeBody({ model }: { model: HistoryDisplayModel }) {
  if (model.type === 'field_change') {
    const tone = historyChangeTone(model.before, model.after);
    return (
      <ChangeRow label={model.fieldLabel}>
        <DiffLine>
          <DiffValue kind="before">{model.before}</DiffValue>
          <ChangeArrow />
          <DiffValue kind="after" tone={tone}>
            {model.after}
          </DiffValue>
        </DiffLine>
      </ChangeRow>
    );
  }

  if (model.type === 'relation_change') {
    return (
      <ChangeRow label={model.fieldLabel}>
        <div className="project-change-history-popover__diff-stack">
          {model.entries.map((entry, index) => (
            <RelationEntryDiff key={index} entry={entry} />
          ))}
        </div>
      </ChangeRow>
    );
  }

  if (model.type === 'project_action') {
    let tone: HistoryChangeTone;
    let label: string;
    switch (model.action) {
      case 'created':
        tone = 'add';
        label = `Создан проект ${model.name}`;
        break;
      case 'deleted':
        tone = 'remove';
        label = `Удален проект ${model.name}`;
        break;
      case 'restored':
        tone = 'add';
        label = `Восстановлен проект ${model.name}`;
        break;
      default: {
        const _exhaustive: never = model.action;
        return _exhaustive;
      }
    }
    return (
      <div className="project-change-history-popover__changes-row-full">
        <ToneBadge tone={tone}>{label}</ToneBadge>
      </div>
    );
  }

  if (model.type === 'session_action') {
    let tone: HistoryChangeTone;
    let label: string;
    switch (model.action) {
      case 'created':
        tone = 'add';
        label = formatSessionCreatedLabel(model.lifecycle);
        break;
      case 'deleted':
        tone = 'remove';
        label = formatSessionDeletedLabel(model.lifecycle);
        break;
      case 'restored':
        tone = 'add';
        label = formatSessionRestoredLabel(model.lifecycle);
        break;
      default: {
        const _exhaustive: never = model.action;
        return _exhaustive;
      }
    }
    return (
      <div className="project-change-history-popover__changes-row-full">
        <ToneBadge tone={tone}>{label}</ToneBadge>
      </div>
    );
  }

  return (
    <div className="project-change-history-popover__changes-row-full">{model.text}</div>
  );
}
